/* Afnan travel sets: product page "in the case" board.
   Tabs switch the selected miniature; a thread joins the tab pins, then the selected tab to its print and note card.
   No animation loop: the thread is redrawn only when something changes (selection, resize, tab scroll). */
(() => {
  'use strict';
  if (window.AfnanTravelCase) return;

  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Small screens: the board is long, so after a tap bring the content that changed into view, clear of any sticky header.
     Only on a tap or click (not arrow keys). */
  const smallScreen = matchMedia('(max-width: 899px)');
  function bringIntoView(el) {
    if (!el || !smallScreen.matches) return;
    const top = el.getBoundingClientRect().top;
    const margin = (parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0) + 12;
    if (Math.abs(top - margin) < 24) return; /* already there */
    window.scrollTo({ top: window.scrollY + top - margin, behavior: reduce ? 'auto' : 'smooth' });
  }

  function setup(root) {
    const ac = new AbortController(), on = { signal: ac.signal };
    const tabsEl = root.querySelector('[data-case-tabs]');
    if (!tabsEl) return () => {};
    const tabs = [...root.querySelectorAll('[data-case-tab]')], panels = [...root.querySelectorAll('[data-case-panel]')];
    const svg = root.querySelector('[data-case-threads]');
    let current = Math.max(0, tabs.findIndex(t => t.getAttribute('aria-selected') === 'true'));

    let frame = 0;
    const queue = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(draw); };
    function draw() {
      if (!svg) return;
      const base = svg.getBoundingClientRect();
      if (!base.width) return;
      svg.setAttribute('viewBox', `0 0 ${base.width} ${base.height}`);
      const pt = el => {
        if (!el || el.closest('[hidden]')) return null;
        const r = el.getBoundingClientRect(), x = r.left + r.width / 2, y = r.top + r.height / 2;
        if (!r.width && !r.height) return null; /* hidden, e.g. the print on small screens */
        if (el.closest('[data-case-tabs]')) { const c = tabsEl.getBoundingClientRect(); if (x < c.left + 4 || x > c.right - 4) return null; }
        return { x: x - base.left, y: y - base.top };
      };
      const paths = [];
      const link = (a, b, active) => {
        if (!a || !b) return;
        const dx = b.x - a.x, dy = b.y - a.y, len = Math.hypot(dx, dy);
        if (len < 18) return;
        const s = { x: a.x + dx / len * 6, y: a.y + dy / len * 6 }, e = { x: b.x - dx / len * 6, y: b.y - dy / len * 6 };
        paths.push(`<path class="atp-thread${active ? ' atp-thread--active' : ''}" d="M${s.x.toFixed(1)} ${s.y.toFixed(1)} Q${((s.x + e.x) / 2).toFixed(1)} ${((s.y + e.y) / 2 + Math.min(18, len * .06)).toFixed(1)} ${e.x.toFixed(1)} ${e.y.toFixed(1)}"/>`);
      };
      const pins = tabs.map(t => pt(t.querySelector('.atp-pin')));
      /* pins in a row are joined; when the tabs wrap onto a new row (small screens) the thread stops there */
      for (let i = 1; i < pins.length; i++) if (pins[i - 1] && pins[i] && Math.abs(pins[i].y - pins[i - 1].y) < 40) link(pins[i - 1], pins[i]);
      const p = panels[current];
      const print = pt(p && p.querySelector('[data-case-print-pin]')), note = pt(p && p.querySelector('[data-case-note-pin]'));
      link(pins[current], print || note, true);
      link(print, note, true);
      svg.innerHTML = paths.join('');
    }

    function select(i, focus) {
      i = (i + tabs.length) % tabs.length;
      current = i;
      tabs.forEach((t, j) => { t.setAttribute('aria-selected', String(j === i)); t.tabIndex = j === i ? 0 : -1; });
      panels.forEach((p, j) => { p.hidden = j !== i; });
      const t = tabs[i];
      if (tabsEl.scrollWidth > tabsEl.clientWidth) tabsEl.scrollTo({ left: t.offsetLeft - (tabsEl.clientWidth - t.offsetWidth) / 2, behavior: 'smooth' });
      if (focus) t.focus({ preventScroll: true });
      queue();
    }

    /* tap a miniature: show its note card (name, families, notes), then bring it into view */
    tabs.forEach((t, i) => t.addEventListener('click', () => { select(i); bringIntoView(panels[i] && panels[i].querySelector('.atp-case__note')); }, on));
    tabsEl.addEventListener('keydown', e => {
      const rtl = document.dir === 'rtl' ? -1 : 1;
      const step = { ArrowRight: rtl, ArrowLeft: -rtl, ArrowDown: 1, ArrowUp: -1 }[e.key];
      if (step) { e.preventDefault(); select(current + step, true); }
      else if (e.key === 'Home') { e.preventDefault(); select(0, true); }
      else if (e.key === 'End') { e.preventDefault(); select(tabs.length - 1, true); }
    }, on);
    tabsEl.addEventListener('scroll', queue, { ...on, passive: true });
    document.addEventListener('shopify:block:select', e => {
      const t = e.target.closest && e.target.closest('[data-case-tab]');
      if (t && root.contains(t)) select(tabs.indexOf(t));
    }, on);
    const ro = new ResizeObserver(queue);
    ro.observe(root);
    root.querySelectorAll('img').forEach(img => { if (!img.complete) img.addEventListener('load', queue, { ...on, once: true }); });
    document.fonts && document.fonts.ready.then(queue);
    select(current);
    return () => { ac.abort(); ro.disconnect(); cancelAnimationFrame(frame); };
  }

  let teardowns = [];
  function init() { teardowns.forEach(fn => fn()); teardowns = [...document.querySelectorAll('[data-atp-case]')].map(setup); }
  window.AfnanTravelCase = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach(t => document.addEventListener(t, () => setTimeout(init, 0)));
})();

/* Afnan travel sets: product page "about" slides. Arrows, swipe and the theme editor's block select move between slides. */
(() => {
  'use strict';
  if (window.AfnanTravelAbout) return;
  function setup(root) {
    const ac = new AbortController(), on = { signal: ac.signal };
    const slides = [...root.querySelectorAll('[data-atp-slide]')];
    if (slides.length < 2) return () => {};
    let current = 0;
    const show = i => {
      current = (i + slides.length) % slides.length;
      slides.forEach((s, j) => { s.classList.toggle('is-active', j === current); s.setAttribute('aria-hidden', String(j !== current)); s.inert = j !== current; });
    };
    root.querySelector('[data-atp-prev]')?.addEventListener('click', () => show(current - (document.dir === 'rtl' ? -1 : 1)), on);
    root.querySelector('[data-atp-next]')?.addEventListener('click', () => show(current + (document.dir === 'rtl' ? -1 : 1)), on);
    let x0 = null, y0 = 0;
    root.addEventListener('pointerdown', e => { if (e.pointerType !== 'mouse') { x0 = e.clientX; y0 = e.clientY; } }, on);
    root.addEventListener('pointerup', e => {
      if (x0 === null) return;
      const dx = e.clientX - x0, dy = e.clientY - y0; x0 = null;
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) show(current + (dx < 0 ? 1 : -1) * (document.dir === 'rtl' ? -1 : 1));
    }, on);
    root.addEventListener('pointercancel', () => { x0 = null; }, on);
    document.addEventListener('shopify:block:select', e => { const i = slides.indexOf(e.target.closest && e.target.closest('[data-atp-slide]')); if (i >= 0) show(i); }, on);
    show(0);
    return () => ac.abort();
  }
  let teardowns = [];
  function init() { teardowns.forEach(fn => fn()); teardowns = [...document.querySelectorAll('[data-atp-about]')].map(setup); }
  window.AfnanTravelAbout = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  ['shopify:section:load', 'shopify:section:unload'].forEach(t => document.addEventListener(t, () => setTimeout(init, 0)));
})();
